// src/pages/services/EducationConsulting.jsx
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Tilt from "react-parallax-tilt";
import {
  FaSearch,
  FaChalkboardTeacher,
  FaGraduationCap,
  FaUniversity,
  FaFileAlt,
  FaMoneyBillWave,
  FaGlobeAmericas,
} from "react-icons/fa";

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut", delay: i * 0.06 },
  }),
};

const steps = [
  {
    icon: <FaSearch size={20} />,
    title: "Step 1: Career & Course Counselling",
    desc:
      "We map your strengths, goals, and budget to shortlist exceptional-fit courses, cities, and timelines.",
  },
  {
    icon: <FaChalkboardTeacher size={20} />,
    title: "Step 2: University Selection & Application",
    desc:
      "Targeted shortlist, documents checklist, forms, and submission — done clearly and on time.",
  },
  {
    icon: <FaGraduationCap size={20} />,
    title: "Step 3: Offers, Scholarships & Next Steps",
    desc:
      "We track decisions, guide scholarships/fee waivers, and plan COE, OSHC, and visa steps.",
  },
];

const inclusions = [
  {
    icon: <FaUniversity className="text-blue-600" />,
    title: "Program Matching",
    desc:
      "Compare UG/PG, diplomas, PYP and pathway options across Australian providers.",
  },
  {
    icon: <FaFileAlt className="text-blue-600" />,
    title: "Documents & SOP",
    desc:
      "Polished CV, SOP/personal statement, references, and portfolio guidance.",
  },
  {
    icon: <FaMoneyBillWave className="text-blue-600" />,
    title: "Scholarships & Fees",
    desc:
      "Shortlist scholarships and clarify tuition, deposits, and refund rules.",
  },
  {
    icon: <FaGlobeAmericas className="text-blue-600" />,
    title: "City & Campus Advice",
    desc:
      "Real-world insights on cost of living, part-time work, transport, and housing.",
  },
];

const destinations = [
  { emoji: "🇦🇺", name: "Australia" },
  { emoji: "🇬🇧", name: "UK" },
  { emoji: "🇺🇸", name: "USA" },
  { emoji: "🇨🇦", name: "Canada" },
  { emoji: "🇳🇿", name: "New Zealand" },
];

export default function EducationConsulting() {
  return (
    <div className="relative min-h-screen bg-white">
      {/* Decorative gradient blobs */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-24 -left-24 h-80 w-80 rounded-full bg-blue-300/30 blur-3xl" />
        <div className="absolute -bottom-20 -right-24 h-96 w-96 rounded-full bg-indigo-300/30 blur-3xl" />
        <div className="absolute top-1/2 left-1/2 h-64 w-64 -translate-x-1/2 -translate-y-1/2 rounded-full bg-sky-200/25 blur-3xl" />
      </div>

      {/* Hero */}
      <section className="relative bg-gradient-to-b from-white/60 to-blue-50/40">
        <div className="mx-auto max-w-7xl px-6 pt-24 pb-12 text-center">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100 text-blue-600"
          >
            <FaUniversity className="text-2xl" />
          </motion.div>

          <motion.h1
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={1}
            className="text-4xl md:text-6xl font-extrabold tracking-tight text-blue-800"
          >
            Education Consulting
          </motion.h1>

          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={2}
            className="mx-auto mt-4 max-w-2xl text-gray-700 text-lg md:text-xl"
          >
            Personalised guidance to pick the right course, university and city —
            then craft strong applications that get offers on time.
          </motion.p>

          <motion.div
            className="mx-auto mt-6 h-1 w-24 rounded-full bg-gradient-to-r from-blue-600 to-indigo-500"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            style={{ originX: 0 }}
          />
        </div>
      </section>

      {/* What's Included (glass cards + tilt) */}
      <section className="mx-auto max-w-7xl px-6 pb-10">
        <motion.h2
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="mb-6 text-center text-2xl md:text-3xl font-bold text-blue-800"
        >
          What’s Included
        </motion.h2>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {inclusions.map((item, i) => (
            <motion.div
              key={item.title}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              custom={i}
            >
              <Tilt
                glareEnable
                glareMaxOpacity={0.15}
                glareColor="#60a5fa"
                glarePosition="all"
                className="h-full"
              >
                <div className="h-full rounded-2xl border border-white/60 bg-white/70 p-6 shadow-[0_6px_30px_rgba(0,0,0,0.06)] backdrop-blur transition hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(59,130,246,0.25)]">
                  <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-blue-100 to-indigo-100 ring-1 ring-blue-200">
                    {item.icon}
                  </div>
                  <h3 className="text-blue-900 font-semibold">{item.title}</h3>
                  <p className="mt-1 text-gray-700 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </Tilt>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How it works (timeline-like cards) */}
      <section className="bg-gradient-to-b from-blue-50/60 to-white">
        <div className="mx-auto max-w-7xl px-6 py-10">
          <motion.h2
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="mb-8 text-center text-2xl md:text-3xl font-bold text-blue-800"
          >
            How It Works
          </motion.h2>

          <div className="grid gap-6 md:grid-cols-3">
            {steps.map((s, i) => (
              <motion.div
                key={s.title}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                custom={i}
                className="relative rounded-2xl border bg-white p-6 shadow-sm transition hover:shadow-md"
              >
                <div className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                  {s.icon}
                </div>
                <h3 className="font-semibold text-blue-800">{s.title}</h3>
                <p className="mt-1 text-gray-700 text-sm leading-relaxed">{s.desc}</p>

                {/* bottom gradient accent */}
                <span className="pointer-events-none absolute inset-x-0 bottom-0 h-[3px] rounded-b-2xl bg-gradient-to-r from-blue-600 via-indigo-500 to-sky-500 opacity-0 transition-opacity duration-300 hover:opacity-100" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Destinations (pills) */}
      <section className="mx-auto max-w-7xl px-6 py-10">
        <motion.h2
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="mb-6 text-center text-2xl md:text-3xl font-bold text-blue-800"
        >
          Popular Study Destinations
        </motion.h2>

        <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-center gap-3">
          {destinations.map((d, i) => (
            <motion.span
              key={d.name}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              custom={i}
              className="inline-flex items-center gap-2 rounded-full border bg-white px-4 py-2 shadow-sm"
            >
              <span className="text-xl">{d.emoji}</span>
              <span className="text-sm font-medium text-blue-800">{d.name}</span>
            </motion.span>
          ))}
        </div>
      </section>

      {/* CTA ribbon */}
      <section className="mx-auto max-w-7xl px-6 pb-16">
        <motion.div
          className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 p-[1px]"
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
        >
          <div className="rounded-2xl bg-white/90 px-6 py-10 md:px-10 md:py-12 backdrop-blur">
            <div className="grid items-center gap-6 md:grid-cols-2">
              <div>
                <h3 className="text-2xl md:text-3xl font-bold text-blue-900">
                  Ready to plan your study future?
                </h3>
                <p className="mt-1 text-gray-600">
                  Book a free session to shortlist courses, understand timelines, and map your next steps.
                </p>
              </div>
              <div className="flex md:justify-end">
                <Link
                  to="/contact"
                  className="inline-flex items-center rounded-xl bg-blue-600 px-5 py-3 font-semibold text-white shadow hover:bg-blue-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
                >
                  Book a Free Consultation
                </Link>
              </div>
            </div>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
